import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  ArrowRight, MapPin, Mail, Globe, Building, CheckCircle2,
  ShieldCheck, ExternalLink, Info, AlertCircle, Phone, Pencil, Star,
} from "lucide-react";
import api from "../lib/api";

/** Regénère une description conforme selon le statut, si la description stockée
 *  reste dans le vieux format ("locale et indépendante basée à … Contactez-la…"). */
function computeDescription(company) {
  const status = company.listing_status || "referenced";
  const stored = (company.description || "").trim();
  const oldStyle = /basée à .* Contactez-l/i.test(stored);

  if (status === "partner" && !oldStyle) return stored;
  if (status === "partner") {
    return (
      `${company.name} est une entreprise partenaire de Déménageur Pro à ${company.city}. ` +
      `Vous pouvez lui adresser directement une demande de devis via notre plateforme.`
    );
  }
  // referenced / removal_pending / autre → texte informatif neutre
  return (
    `${company.name} est une entreprise de déménagement référencée à ${company.city}` +
    (company.postal_code ? ` (${company.postal_code})` : "") +
    ` à partir de données professionnelles publiques. Cette fiche est fournie à titre informatif. ` +
    `Pour demander un devis via Déménageur Pro, votre demande sera transmise uniquement à des ` +
    `entreprises partenaires ou autorisées à la recevoir.`
  );
}

export default function CompanyDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/companies/slug/${slug}`)
      .then((r) => {
        setCompany(r.data);
        document.title = `${r.data.name} — Déménageur ${r.data.city}`;
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return <div className="container mx-auto px-6 py-20 text-center text-sm text-[#6B7280]" data-testid="company-loading">Chargement…</div>;
  }
  if (notFound || !company) {
    return (
      <div className="container mx-auto px-6 py-20 text-center" data-testid="company-not-found">
        <h1 className="font-heading font-extrabold text-3xl mb-4 text-[#2C3E50]">Entreprise introuvable</h1>
        <Link to="/" className="btn-primary">Retour à l'accueil</Link>
      </div>
    );
  }

  const status = company.listing_status || "referenced";
  const isPartner = status === "partner" || status === "partner_verified";
  const isVerified = status === "partner_verified";
  const isPendingRemoval = status === "removal_pending";
  const description = computeDescription(company);

  return (
    <div className="animate-fade-up">
      <section className="bg-gradient-to-b from-[#F7F9FB] to-white py-12 md:py-16">
        <div className="container mx-auto px-6 lg:px-10">
          <div className="text-xs text-[#6B7280] mb-6 flex items-center gap-2">
            <Link to="/" className="hover:text-[#2BD391]">Accueil</Link>
            <span>/</span>
            <Link to={`/demenageurs/${company.city_slug}`} className="hover:text-[#2BD391]">{company.city}</Link>
            <span>/</span>
            <span className="text-[#2C3E50] font-semibold">{company.name}</span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-8">
              {/* Badge de statut juridique */}
              {isPartner ? (
                <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-[#2BD391] text-white text-[10px] font-bold uppercase tracking-widest rounded-full mb-4" data-testid="badge-partner">
                  <ShieldCheck className="w-3 h-3" /> Entreprise partenaire
                </div>
              ) : (
                <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-[#F0F2F5] text-[#5C6B7A] text-[10px] font-bold uppercase tracking-widest rounded-full mb-4 border border-[#E5E7EB]" data-testid="badge-referenced">
                  <Info className="w-3 h-3" /> Entreprise référencée
                </div>
              )}

              <h1 className="font-heading font-extrabold text-4xl md:text-6xl tracking-tight text-[#2C3E50] leading-tight mb-3" data-testid="company-name">
                {company.name}
              </h1>

              {/* Phrase informative sous le nom (référencée seulement) — Section 8 du briefing légal */}
              {!isPartner && (
                <div className="mb-5 max-w-2xl text-xs text-[#5C6B7A] bg-[#F7F9FB] border border-[#E5E7EB] rounded-lg p-3" data-testid="referenced-notice">
                  <p className="italic">
                    Fiche référencée à titre informatif à partir de données professionnelles publiques.
                    Cette entreprise n'est pas partenaire de Déménageur Pro, sauf mention explicite.
                    Déménageur Pro ne garantit pas la disponibilité actuelle de l'entreprise.
                    {company.last_verified_at && (
                      <> Informations vérifiées pour la dernière fois le {new Date(company.last_verified_at).toLocaleDateString("fr-FR")}.</>
                    )}
                  </p>
                </div>
              )}

              {isPendingRemoval && (
                <div className="mb-5 flex items-start gap-2 p-3 bg-orange-50 border border-orange-200 rounded-lg text-xs text-orange-800" data-testid="pending-removal-notice">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>Une demande de suppression est en cours d'examen pour cette fiche.</span>
                </div>
              )}

              <div className="flex items-center gap-3 mb-6 flex-wrap">
                {/* Avis Google — uniquement pour les partenaires avec rating renseigné */}
                {isPartner && company.rating > 0 && (
                  <div
                    className="inline-flex items-center gap-2 bg-white border-2 border-[#E5E7EB] rounded-2xl px-4 py-2 shadow-sm"
                    data-testid="rating-badge"
                    title={company.rating_source || "Note constatée publiquement sur Google au moment du référencement."}
                  >
                    <span className="bg-[#4285F4] text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full">Google</span>
                    <Star className="w-4 h-4 fill-[#FBBC04] text-[#FBBC04]" />
                    <span className="font-bold text-base text-[#2C3E50]">{company.rating.toFixed(1)}</span>
                    <span className="text-xs text-[#6B7280]">({company.review_count || 0} avis)</span>
                  </div>
                )}
                <div className="flex items-center gap-1.5 text-sm text-[#6B7280]">
                  <MapPin className="w-3.5 h-3.5" /> {company.city}, {company.postal_code}
                </div>
              </div>

              {/* Mention légale sur la source des avis (partenaire uniquement) */}
              {isPartner && company.rating > 0 && (
                <p className="text-[11px] text-[#8A98A5] italic -mt-3 mb-5" data-testid="rating-source-notice">
                  {company.rating_source || "Note constatée publiquement sur Google au moment du référencement, avec accord de l'entreprise."}
                </p>
              )}

              <p className="text-lg text-[#2C3E50] leading-relaxed mb-8" data-testid="company-description">
                {description}
              </p>

              {company.services?.length > 0 && (
                <div className="mb-10">
                  <div className="eyebrow mb-4">Services proposés</div>
                  <div className="flex flex-wrap gap-2.5">
                    {company.services.map((s) => (
                      <span key={s} className="px-4 py-2 bg-white border border-[#E5E7EB] rounded-full text-sm font-medium text-[#2C3E50] flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-[#2BD391]" />
                        {s}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="bg-[#F7F9FB] rounded-2xl p-6 md:p-8">
                <div className="eyebrow mb-4">Informations légales</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <InfoRow icon={Building} label="Forme juridique" value={company.legal_form || "—"} />
                  <InfoRow icon={MapPin} label="Adresse" value={company.address || "—"} />
                  <InfoRow icon={MapPin} label="Département" value={company.department || "—"} />
                  {company.siret && <InfoRow icon={Building} label="SIRET" value={company.siret} />}
                </div>
              </div>

              {/* Mention légale annuaire */}
              <div className="mt-6 bg-white border border-[#E5E7EB] rounded-2xl p-5 text-xs text-[#6B7280] leading-relaxed" data-testid="b2b-notice">
                <p className="mb-3">
                  <strong className="text-[#2C3E50]">Fiche publiée à titre informatif.</strong>{" "}
                  {isPartner ? (
                    <>Cette entreprise est partenaire de Déménageur Pro.</>
                  ) : (
                    <>
                      Cette fiche est publiée à titre informatif à partir d'informations
                      professionnelles accessibles publiquement (INSEE Sirene, Pappers, site
                      officiel). L'entreprise mentionnée n'est pas nécessairement partenaire
                      de Déménageur Pro, sauf indication contraire.
                    </>
                  )}
                </p>
                <p>
                  Si vous êtes le représentant de cette entreprise et souhaitez modifier,
                  corriger ou supprimer cette fiche,{" "}
                  <Link
                    to={`/fiche-entreprise/modifier?slug=${company.slug}&name=${encodeURIComponent(company.name)}`}
                    className="text-[#1AA66C] font-semibold hover:underline"
                    data-testid="b2b-notice-link"
                  >
                    contactez-nous ici
                  </Link>
                  .{" "}
                  <Link to="/mentions-legales-b2b" className="text-[#6B7280] underline hover:text-[#2C3E50]">
                    En savoir plus sur l'annuaire.
                  </Link>
                </p>
              </div>
            </div>

            {/* ─── COLONNE DROITE ─── */}
            <div className="lg:col-span-4">
              <div className="sticky top-24 space-y-4">
                {isPartner ? (
                  /* Bloc devis partenaire (vert plein) */
                  <div className="bg-gradient-to-br from-[#2BD391] to-[#1AA66C] text-white rounded-3xl p-7 shadow-[0_12px_32px_-8px_rgba(43,211,145,0.35)]" data-testid="partner-quote-block">
                    <div className="eyebrow text-white/80 mb-3">Demande de devis</div>
                    <h2 className="font-heading font-extrabold text-2xl leading-tight mb-2 text-white" style={{ color: "white" }}>
                      Recevez un devis personnalisé
                    </h2>
                    <p className="text-sm text-white/90 mb-6">
                      Cette entreprise est partenaire de Déménageur Pro.
                    </p>
                    <button
                      onClick={() => navigate(`/devis?company=${company.slug}`)}
                      className="w-full bg-white text-[#1AA66C] hover:bg-[#1A2A3A] hover:text-white rounded-full py-4 font-bold text-sm uppercase tracking-wider transition-all flex items-center justify-center gap-2"
                      data-testid="company-quote-btn"
                    >
                      Demander un devis <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  /* Bloc devis neutre pour entreprises référencées */
                  <div className="bg-white border-2 border-[#E5E7EB] rounded-3xl p-7" data-testid="referenced-quote-block">
                    <div className="eyebrow mb-3">Devis via la plateforme</div>
                    <h2 className="font-heading font-extrabold text-2xl leading-tight mb-3 text-[#2C3E50]">
                      Besoin d'un devis déménagement ?
                    </h2>
                    <p className="text-xs text-[#6B7280] leading-relaxed mb-5">
                      Déménageur Pro peut vous mettre en relation avec des entreprises
                      partenaires de déménagement. Cette fiche est publiée à titre informatif
                      et l'entreprise affichée n'est pas nécessairement partenaire de la
                      plateforme.
                    </p>
                    <button
                      onClick={() => navigate("/devis")}
                      className="w-full bg-[#2BD391] hover:bg-[#1AA66C] text-white rounded-full py-3.5 font-bold text-[13px] uppercase tracking-wider transition-all flex items-center justify-center gap-2"
                      data-testid="referenced-quote-btn"
                    >
                      Demander des devis auprès de nos partenaires
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                )}

                {/* Bloc contact / coordonnées publiques */}
                <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6 space-y-3">
                  <div className="eyebrow mb-2">
                    {isPartner ? "Contact" : "Coordonnées publiques"}
                  </div>
                  {isPartner ? (
                    <p className="text-xs text-[#6B7280] leading-relaxed">
                      Contactez cette entreprise partenaire via notre formulaire pour recevoir
                      un devis personnalisé.
                    </p>
                  ) : (
                    <p className="text-xs text-[#6B7280] leading-relaxed">
                      Cette fiche est publiée à titre informatif à partir de données
                      professionnelles publiques. L'entreprise mentionnée n'est pas
                      nécessairement partenaire de Déménageur Pro. Pour contacter directement
                      cette entreprise, vous pouvez utiliser les coordonnées publiques
                      ci-dessous ou visiter son site officiel lorsqu'il est disponible.
                    </p>
                  )}

                  {company.phone && (
                    <a href={`tel:${company.phone.replace(/\s+/g, "")}`} className="flex items-center gap-3 text-sm text-[#2C3E50] hover:text-[#2BD391] pt-3 mt-3 border-t border-[#F0F2F5]" data-testid="company-phone">
                      <div className="w-9 h-9 bg-[#E8FBF3] rounded-full flex items-center justify-center"><Phone className="w-4 h-4 text-[#2BD391]" /></div>
                      <span className="font-medium">{company.phone}</span>
                    </a>
                  )}
                  {isPartner && company.email && (
                    <a href={`mailto:${company.email}`} className="flex items-center gap-3 text-sm text-[#2C3E50] hover:text-[#2BD391]" data-testid="company-email">
                      <div className="w-9 h-9 bg-[#E8FBF3] rounded-full flex items-center justify-center"><Mail className="w-4 h-4 text-[#2BD391]" /></div>
                      <span className="font-medium truncate">{company.email}</span>
                    </a>
                  )}
                  {company.website && (
                    <a
                      href={company.website}
                      target="_blank"
                      rel="noreferrer nofollow"
                      className="mt-2 w-full inline-flex items-center justify-center gap-2 text-sm font-semibold border-2 border-[#2BD391] text-[#1AA66C] hover:bg-[#2BD391] hover:text-white rounded-full px-5 py-3 transition-all"
                      data-testid="company-website-btn"
                    >
                      <Globe className="w-4 h-4" />
                      Visiter le site officiel
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  )}
                </div>

                {/* Bouton correction/retrait */}
                <Link
                  to={`/fiche-entreprise/modifier?slug=${company.slug}&name=${encodeURIComponent(company.name)}`}
                  className="block text-center text-xs text-[#6B7280] hover:text-[#2C3E50] py-3 border-t border-[#E5E7EB]"
                  data-testid="edit-listing-link"
                >
                  <Pencil className="w-3 h-3 inline mr-1" />
                  Vous représentez cette entreprise ? Modifier ou retirer cette fiche
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

const InfoRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3">
    <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center shrink-0">
      <Icon className="w-4 h-4 text-[#2BD391]" />
    </div>
    <div className="min-w-0">
      <div className="text-xs uppercase tracking-wider text-[#6B7280]">{label}</div>
      <div className="font-semibold text-[#2C3E50] break-words">{value}</div>
    </div>
  </div>
);
